import { createContext } from '@fluentui/react-context-selector';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';

/**
 * Internal component state managed by the command model
 */
export interface MultiplesGeneratorState {
  multiplier: number | null;
}

/**
 * External resources that will be available to commands (API handlers etc)
 */
export interface MultiplesGeneratorAdditionalCommandContext {
  generateRandomNumber: () => number;
}

/**
 * External data available to the component
 */
export interface MultiplesGeneratorOuterState {
  baseNumber: number;
}

// Let's create short aliases because all 3 following types are
// repeatedly used to create specific types for given command model.
type S = MultiplesGeneratorState;
type C = MultiplesGeneratorAdditionalCommandContext;
type O = MultiplesGeneratorOuterState;

// specific types for given command model, to avoid using the three-argument generic widely in userland
// See doc comment for given generic types to understand what they are good for.
export type MultiplesGeneratorCommandContext = CommandModelCommandContext<
  S,
  C,
  O
>;
export type MultiplesGeneratorCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<S, C, O, TCommandArgs>;
export type MultiplesGeneratorDispatch = CommandModelCommandDispatcher<S, C, O>;
export type MultiplesGeneratorMergedState = CommandModelMergedState<S, O>;
export type MultiplesGeneratorModel = CommandModel<S, C, O>;

export const multiplesGeneratorInitialState: MultiplesGeneratorState = {
  multiplier: null,
};

/**
 * Context to provide the hook to react components subtree to avoid prop-drilling
 *
 * The context must be created by `createContext` from `@fluentui/react-context-selector`
 * to allow using context selectors, so that all components consuming the context don't have to rerender
 * on every model change.
 *
 * Such context should be consumed with `useContextSelectors` function, resp. its specific variant
 * `useMultiplesGeneratorContextSelectors` below.
 *
 * No default value:
 * https://medium.com/@rivoltafilippo/typing-react-context-to-avoid-an-undefined-default-value-2c7c5a7d5947
 */
export const MultiplesGeneratorContext = createContext<MultiplesGeneratorModel>(
  undefined!,
);

export const useMultiplesGeneratorContextSelectors: ContextSelectorsHook<
  MultiplesGeneratorModel
> = (selectors) => useContextSelectors(MultiplesGeneratorContext, selectors);
